import React, { useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { getMerchantGroupsBySearch } from '../../api/merchant';
import styles from './ChangeMerchantGroupModal.module.scss';
import useComponentState from '../../utils/useComponentState';
import useDebounce from '../../utils/useDebounce';

const FILTER_OPTIONS = [
  { value: 'ID', label: 'Filter by ID' },
  { value: 'DESCRIPTION', label: 'Filter by Description' },
];

const ChangeMerchantGroupModal = ({
  show,
  handleClose,
  token,
  onGroupSelect,
  currentGroup,
}) => {
  const [state, setState] = useComponentState({
    searchTerm: '',
    groups: [],
    loading: false,
    error: '',
    filterType: 'ID',
  });

  const debouncedSearchTerm = useDebounce(state.searchTerm, 500);

  // Track the latest request so stale responses are ignored
  const latestReqIdRef = useRef(0);
  // support aborting older requests if api util accepts AbortController
  const abortCtrlRef = useRef(null);

  useEffect(() => {
    const term = debouncedSearchTerm.trim();

    // Clear results if empty search
    if (!term) {
      setState({ groups: [], error: '', loading: false });
      // cancel any previous in-flight call
      if (abortCtrlRef.current) abortCtrlRef.current.abort();
      return;
    }

    const reqId = ++latestReqIdRef.current;

    // If getMerchantGroupsBySearch supports axios signal, wire it in
    const controller = new AbortController();
    abortCtrlRef.current = controller;

    setState({ loading: true, error: '' });

    (async () => {
      try {
        // If getMerchantGroupsBySearch doesn’t take signal, remove it and rely on reqId guard
        const response = await getMerchantGroupsBySearch(
          token,
          term,
          state.filterType,
          { signal: controller.signal }
        );

        // Ignore stale responses
        if (reqId !== latestReqIdRef.current) return;

        setState({
          groups: Array.isArray(response) ? response : [],
          loading: false,
        });
      } catch (err) {
        // Ignore errors from aborted/stale requests
        if (reqId !== latestReqIdRef.current) return;
        if (err?.name === 'AbortError') return;

        setState({
          error: err?.message || 'Failed to fetch merchant groups',
          loading: false,
        });
      }
    })();

    // Cleanup: abort when inputs change/unmount
    return () => {
      controller.abort();
    };
  }, [debouncedSearchTerm, state.filterType, token]);

  const handleCloseWithStateReset = () => {
    handleClose();
    setState({
      searchTerm: '',
      groups: [],
      error: '',
      loading: false,
      filterType: 'ID',
    });
    latestReqIdRef.current = 0;
    if (abortCtrlRef.current) abortCtrlRef.current.abort();
  };

  const handleGroupSelect = (group) => {
    onGroupSelect(group);
    handleCloseWithStateReset();
  };

  return (
    <Modal
      className={styles.changeMerchantGroupModal}
      show={show}
      onHide={handleCloseWithStateReset}
      centered
      size='lg'
    >
      <Modal.Header closeButton>
        <Modal.Title>Change Merchant Group</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentGroup && (
          // Display current group info
          <div className={styles.GroupInfo}>
            <h6 className='mb-3'>Current Merchant Group</h6>
            <div className='mb-3 p-3 border rounded'>
              <div>
                <strong>ID:</strong> {currentGroup.id}
              </div>
              <div>
                <strong>Name:</strong> {currentGroup.name || 'N/A'}
              </div>
              <div>
                <strong>Description:</strong>{' '}
                {currentGroup.description || 'N/A'}
              </div>
            </div>
          </div>
        )}

        <div className={styles.GroupInfo}>
          <h6 className='mb-3'>Change Merchant Group</h6>
          <div className='d-flex gap-2 mb-3'>
            <Form.Group style={{ minWidth: '150px' }}>
              <Form.Control
                as='select'
                className='form-select'
                value={state.filterType}
                onChange={(e) => setState({ filterType: e.target.value })}
              >
                {/* Render filter options */}
                {FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group className='flex-grow-1'>
              <Form.Control
                type='text'
                placeholder='Search merchant groups...'
                value={state.searchTerm}
                onChange={(e) => setState({ searchTerm: e.target.value })}
              />
            </Form.Group>
          </div>
        </div>

        {state.loading && (
          <div className={styles.loading}>
            <Spinner animation='border' size='sm' />
          </div>
        )}
        {state.error && <div className={styles.error}>{state.error}</div>}

        <div className={styles.groupsList}>
          {/* Render group items */}
          {state.groups.map((group) => (
            <div
              key={group.id}
              className={styles.groupItem}
              onClick={() => handleGroupSelect(group)}
              role='button'
            >
              <span>
                {state.filterType === 'ID'
                  ? group.id
                  : `${group.description} (${group.id})`}
              </span>
              {currentGroup?.id === group.id && (
                <span className={styles.current}>(Current)</span>
              )}
            </div>
          ))}
          {state.groups.length === 0 && !state.loading && state.searchTerm && (
            <div className='text-center text-muted'>No groups found</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleCloseWithStateReset}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeMerchantGroupModal;
